import React, { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Box, Breadcrumbs, Button, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, FormHelperText, InputLabel, MenuItem, Paper, Select, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material'
import 'json-diff-kit/dist/viewer.css';
import AppContext from '../../state/AppContext';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Header from '../../template/Header';
import { Editor } from '@monaco-editor/react';
import PlaylistAddCheckCircleIcon from '@mui/icons-material/PlaylistAddCheckCircle';
import APIInvoke from '../../utils/APIInvoke';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import toast from 'react-hot-toast';
import { hasValue } from '../../utils/StringUtils';
import { setLoading } from '../../template/app-actions';
import CircularProgress from '@mui/material/CircularProgress';
import GrantBreadcrumbs from '../../template/GrantBreadcrumbs';

const EditCollectionRule = (props) => {

    const [state, setState] = useState({
        enable: true,
        name: "",
        description: "",
        expression: "",
        example: "",
        previousExample: "",
        webhookId: "",
        notifyTo: [],
        autoEvaluate: true,
        tab: 0,
        validEmail: true,
        useCustomPayload: false,
        customPayloadExpression: ""
    })

    const [webhooks, setWebhooks] = useState([])
    const [testResult, setTestResult] = useState(null)
    const [testPayloadResult, setTestPayloadResult] = useState(null)
    const { collection, documentId, ruleId } = useParams()
    const { appContext } = useContext(AppContext)
    const project = appContext.project
    const completionProviderRef = useRef(null);
    const monacoRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {

        const loadRule = async () => {
            try {
                setLoading(true)
                const rawWebhooksResponse = await APIInvoke.invokeGET(`/projects/${project}/webhooks`);
                const webhooksResponse = await rawWebhooksResponse.json();
                setWebhooks(webhooksResponse.body)

                if (!ruleId) return;

                const ruleRawResponse = await APIInvoke.invokeGET(`/projects/${project}/collections/${collection}/rules/${ruleId}`)
                const ruleResponse = await ruleRawResponse.json();

                const webhook = webhooksResponse.body.find(webhook => webhook.id === ruleResponse.body.webhookId);

                setState({
                    ...state,
                    ...ruleResponse.body,
                    webhook: webhook,
                    webhookId: webhook?.id
                });
            } finally {
                setLoading(false)
            }


        }
        loadRule();
    }, [project, collection, documentId])


    useEffect(() => {
        if (state.autoEvaluate) {
            validateExpression();
        }
    }, [state.example, state.previousExample, state.expression, state.autoEvaluate])

    useEffect(() => {
        if (state.useCustomPayload) {
            validateCustomPayloadExpression();
        }
    }, [state.example, state.customPayloadExpression, state.useCustomPayload])


    useEffect(() => {
        setupAutocompletion(null, monacoRef.current);
    }, [state.example, state.previousExample, monacoRef.current]);

    const validateCustomPayloadExpression = async () => {
        try {
            if (!hasValue(state.example) || !hasValue(state.customPayloadExpression)) {
                return;
            }

            let example = null;
            try {
                example = JSON.parse(state.example);
            } catch (e) {
            }

            const request = {
                project: project,
                collection: collection,
                example: example,
                expression: state.customPayloadExpression,
            }

            const rawResponse = await APIInvoke.invokePOST(`/projects/${project}/collections/${collection}/expression/test`, request);
            const response = await rawResponse.json();
            setTestPayloadResult(response.body)


        } catch (error) {
        }
    }


    const validateExpression = async () => {
        try {
            if (!hasValue(state.example) || !hasValue(state.expression)) {
                return;
            }

            let example = null;
            try {
                example = JSON.parse(state.example);
            } catch (e) {
            }

            let previousExample = null;
            try {
                previousExample = JSON.parse(state.previousExample);
            } catch (e) {
            }

            const request = {
                expression: state.expression,
                rulesExample: example,
                rulesPreviewExample: previousExample
            }

            const rawResponse = await APIInvoke.invokePOST(`/projects/${project}/collections/${collection}/rules/expression/test`, request);
            const response = await rawResponse.json();
            setTestResult(response.body)

        } catch (error) {
        }
    }

    const onChangeRule = (e) => {
        setState({ ...state, [e.target.name]: e.target.value })
    }

    const onSave = async () => {
        const request = {
            enable: state.enable,
            name: state.name,
            description: state.description,
            expression: state.expression,
            example: state.example,
            previousExample: state.previousExample,
            webhookId: state.webhookId,
            notifyTo: state.notifyTo || [],
            useCustomPayload: state.useCustomPayload,
            customPayloadExpression: state.customPayloadExpression
        }

        const rawResponse = state.id
            ? await APIInvoke.invokePUT(`/projects/${project}/collections/${collection}/rules/${state.id}`, request)
            : await APIInvoke.invokePOST(`/projects/${project}/collections/${collection}/rules`, request);
        const response = await rawResponse.json();

        if (response.ok) {
            setState({ ...state, id: response.body.id })
            navigate(`/projects/${project}/collections/${collection}/rules/${response.body.id}`)
            toast.success("Rule saved successfully")
        } else {
            toast.error(response.message)
        }
    }

    const validateEmailInput = (event) => {
        if (event.key === "Enter") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(event.target.value)) {
                setState({ validEmail: false })
            } else {
                // Verificar si el email ya existe en la lista (ignorando mayúsculas/minúsculas)
                const emailLowerCase = event.target.value.toLowerCase();
                if (!state.notifyTo.some(email => email.toLowerCase() === emailLowerCase)) {
                    setState({
                        ...state,
                        validEmail: true,
                        notifyTo: [...state.notifyTo, event.target.value]
                    })
                }
            }
            // Limpiar el input después de procesar
            event.target.value = '';
        }
    }

    const onChangeWebhook = (event) => {
        const webhookId = event.target.value;
        const webhook = webhooks.find(x => x.id === webhookId);
        setState({
            ...state,
            webhookId: webhookId,
            webhook: webhook
        })
    }


    const fetchRandomDocumentExample = async () => {
        try {
            setState({ ...state, exampleLoading: true })
            const rawResponse = await APIInvoke.invokeGET(`/projects/${project}/collections/${collection}/documents/random`);
            const response = await rawResponse.json();
            setState({ ...state, exampleLoading: false, example: JSON.stringify(response.body.payload, null, 2) })
        } catch (error) {
            setState({ ...state, exampleLoading: false })
            toast.error("Error fetching random document example")
        }
    }

    const fetchRandomDocumentPreviousExample = async () => {
        try {
            setState({ ...state, previousExampleLoading: true })
            const rawResponse = await APIInvoke.invokeGET(`/projects/${project}/collections/${collection}/documents/random`);
            const response = await rawResponse.json();
            setState({ ...state, previousExampleLoading: false, previousExample: JSON.stringify(response.body.payload, null, 2) })
        } catch (error) {
            setState({ ...state, previousExampleLoading: false })
            toast.error("Error fetching random document example")
        }
    }

    const removeNotifyTo = (user) => {
        setState({ ...state, notifyTo: state.notifyTo.filter(u => u !== user) })
    }

    const setupAutocompletion = (editor, monaco) => {
        if (!monaco) return;

        if (completionProviderRef.current) {
            completionProviderRef.current.dispose();
        }

        let suggestionsPrev = [];
        let suggestions = [];
        try {
            let jsonDoc = null;
            try {
                jsonDoc = JSON.parse(state.example);
                suggestions = getNestedProperties(jsonDoc);
            } catch (error) {
                jsonDoc = {}
            }

            let jsonDocPrev = null;
            try {
                jsonDocPrev = JSON.parse(state.previousExample);
                suggestionsPrev = getNestedProperties(jsonDocPrev);
            } catch (error) {
                jsonDocPrev = {}
            }


            // Función recursiva para obtener todas las propiedades anidadas
            const getNestedProperties = (obj, prefix = '') => {
                return Object.entries(obj).reduce((acc, [key, value]) => {
                    const fullPath = prefix ? `${prefix}.${key}` : key;
                    acc.push({
                        label: key,
                        kind: monaco.languages.CompletionItemKind.Property,
                        insertText: key,
                        detail: typeof value,
                        documentation: fullPath
                    });

                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        acc.push(...getNestedProperties(value, fullPath));
                    }

                    return acc;
                }, []);
            };


            // Variables predefinidas
            const predefinedVariables = [
                {
                    label: '$',
                    kind: monaco.languages.CompletionItemKind.Variable,
                    insertText: '$',
                    detail: 'Current document',
                    documentation: 'This variable represents the current document'
                }, {
                    label: '$prev',
                    kind: monaco.languages.CompletionItemKind.Variable,
                    insertText: '$prev',
                    detail: 'Previous document version',
                    documentation: 'This variable represents the previous document version'
                },
            ];

            // Registrar el nuevo proveedor y guardar la referencia
            completionProviderRef.current = monaco.languages.registerCompletionItemProvider('java', {
                triggerCharacters: ['$', '.'],
                provideCompletionItems: (model, position) => {
                    const textUntilPosition = model.getValueInRange({
                        startLineNumber: position.lineNumber,
                        startColumn: 1,
                        endLineNumber: position.lineNumber,
                        endColumn: position.column
                    });

                    // Mostrar variables predefinidas al inicio
                    if (textUntilPosition === '') {
                        return {
                            suggestions: predefinedVariables
                        };
                    }

                    // Solo mostrar sugerencias después de "$."
                    if (textUntilPosition.endsWith('$.')) {
                        return {
                            suggestions: suggestions.filter(s => !s.documentation.includes('.'))
                        };
                    }

                    if (textUntilPosition.endsWith('$prev.')) {
                        return {
                            suggestions: suggestionsPrev.filter(s => !s.documentation.includes('.'))
                        };
                    }

                    // Si estamos en medio de una propiedad anidada (ej: $.contacto.)
                    const match = textUntilPosition.match(/\$\.([\w.]+)\.$/);
                    if (match) {
                        const currentPath = match[1];
                        const nestedSuggestions = suggestions.filter(s => {
                            const suggestionPath = s.documentation;
                            const pathParts = suggestionPath.split('.');
                            const currentParts = currentPath.split('.');

                            return suggestionPath.startsWith(currentPath + '.') &&
                                pathParts.length === currentParts.length + 1;
                        }).map(s => ({
                            ...s,
                            insertText: s.documentation.split('.').pop()
                        }));

                        return {
                            suggestions: nestedSuggestions
                        };
                    }

                    // Mostrar variables predefinidas después de un espacio o al inicio de una nueva línea
                    if (textUntilPosition.match(/^\s*$/) || textUntilPosition.endsWith(' ')) {
                        return {
                            suggestions: predefinedVariables
                        };
                    }

                    return { suggestions: [] };
                }
            });
        } catch (e) {
            console.error('Error parsing document example:', e);
        }
    }

    const handleEditorDidMount = (editor, monaco) => {
        monacoRef.current = monaco;
        setupAutocompletion(editor, monaco);
    }


    const changeTab = (event, newValue) => {
        setState({ ...state, tab: newValue });
    };

    const onChange = (event) => {
        const { name, value, checked, type } = event.target;

        if (type === "checkbox") {
            setState({ ...state, [name]: checked })
        } else {
            setState({ ...state, [name]: value })
        }
    }

    return (
        <div className="">
            <Header
                title={<span>{state?.id ? state?.name : "New rule"}<span className="text-xs text-gray-700">/ {documentId} </span></span>}
                backLink={`/projects/${project}/collections/${collection}/edit?tab=rules`}
                state={{ tab: "rules" }} >

                <Breadcrumbs aria-label="breadcrumb" separator="/" className="!text-gray-600" >
                    <Typography ><HomeOutlinedIcon className="text-gray-600" fontSize="small" /></Typography>
                    <GrantBreadcrumbs permisos={[Permissions.COLLECTION_R]} text="Collections" link={`/projects/${project}/collections`} />
                    <GrantBreadcrumbs permisos={[Permissions.COLLECTION_W]} text={collection} link={`/projects/${project}/collections/${collection}/edit?tab=rules`} />
                    <Typography ><span className="text-gray-600">{documentId}</span></Typography>
                </Breadcrumbs>
            </Header>

            <div className="content grid gap-8">

                <div className="flex justify-end">
                    <FormControlLabel control={<Switch name="enable" checked={state.enable} onChange={onChange} />} label={state.enable ? "Enabled" : "Disabled"} />
                </div>

                <Accordion expanded={true} >
                    <AccordionSummary className="!bg-gray-100 border-b !cursor-default w-full" id={state.id}  >
                        <div className="w-full flex flex-row justify-between items-center">
                            <span>Collection Rule</span>
                            <FormControl variant="outlined" size="small" >
                                <FormControlLabel
                                    labelPlacement="start"
                                    label="Auto evaluate"
                                    control={
                                        <Checkbox
                                            id="autoEvaluate"
                                            name="autoEvaluate"
                                            className="!p-0 !pr-2"
                                            checked={state.autoEvaluate}
                                            onChange={(e) => { setState({ ...state, autoEvaluate: e.target.checked }) }}
                                        />
                                    }
                                />
                            </FormControl>
                        </div>
                    </AccordionSummary>
                    <AccordionDetails className="p-4">
                        <div className="grid gap-4 py-4">

                            <div className="grid gap-2">
                                <p>Email notification</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex flex-row gap-2">
                                        <FormControl variant="outlined" size="large" fullWidth={true}>
                                            <TextField
                                                id="email"
                                                error={state.validEmail === false}
                                                type="email"
                                                size="medium"
                                                label="Entrar email user to notify"
                                                helperText={"Enter the email of the users who will be notified when a rule is triggered"}
                                                onKeyDown={validateEmailInput}
                                            />
                                        </FormControl>
                                        {/* <Button variant="contained" color="primary" className="h-14" onClick={() => document.getElementById('email').dispatchEvent(new KeyboardEvent('keydown', {'key': 'Enter'}))} >ADD</Button> */}
                                    </div>
                                </div>

                                <Stack direction="row" spacing={1}>
                                    {state.notifyTo && state.notifyTo.map(user => (
                                        <Chip key={user} label={user} variant="outlined" onDelete={e => removeNotifyTo(user)} />
                                    ))}
                                </Stack>

                                <div className="grid gap-2">
                                    <p>Webhook notification</p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormControl>
                                            <InputLabel id="webhookLabel" >Webhook Endpoint</InputLabel>
                                            <Select name="webhook" labelId="webhookLabel" value={state.webhookId} onChange={onChangeWebhook} >
                                                <MenuItem value=""><em>Select</em></MenuItem>
                                                {webhooks.map(webhook => (
                                                    <MenuItem key={webhook.id} value={webhook.id}>{webhook.name}</MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>{state.webhook?.url}</FormHelperText>
                                        </FormControl>
                                    </div>
                                </div>
                                <div className="border-b border-gray-300 my-4" />
                            </div>

                            <p>Document Rule</p>
                            <div className="grid grid-cols-2 gap-4">
                                <Box className="h-full grid gap-4">
                                    <Box sx={{ width: '100%' }}>
                                        <Paper sx={{ width: '100%' }}>
                                            <Box sx={{ width: '100%' }}>
                                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                                    <Tabs value={state.tab} onChange={changeTab} className="bg-gray-100">
                                                        <Tab className={state.tab === 0 ? "!bg-green-50" : ""} label={<p className="flex items-center gap-1">Current Document<Tooltip title="This is a example corresponds to the new state of the document created"><HelpOutlineIcon /></Tooltip></p>} iconPosition="start" />
                                                        <Tab className={state.tab === 1 ? "!bg-green-50" : ""} label={<p className="flex items-center gap-1">Preview Document<Tooltip title="This is a example corresponds to the previous version of the document created, this can be used to compare the changes between the new and the previous version, for example, if you want to check if the 'status' has changed, you can use $.status != $prev.status"><HelpOutlineIcon /></Tooltip></p>} iconPosition="start" />
                                                    </Tabs>

                                                    {state.tab === 0 && (
                                                        <div className="grid gap-2 p-4">
                                                            <Box className="h-full !w-full overflow-hidden">
                                                                <Paper className="h-full !w-full">
                                                                    <Editor
                                                                        options={{
                                                                            selectOnLineNumbers: true,
                                                                            automaticLayout: true,
                                                                            minimap: {
                                                                                enabled: false
                                                                            },
                                                                            lineNumbers: 'on'
                                                                        }}
                                                                        width="100%"
                                                                        height="300px"
                                                                        language="json"
                                                                        theme="git"
                                                                        className="h-full !w-full !overflow-hidden"
                                                                        value={state.example}
                                                                        onChange={(newValue, e) => onChange({ target: { name: "example", type: "text", value: newValue } })}
                                                                    />
                                                                </Paper>
                                                            </Box>
                                                            <p className="text-gray-700 text-xs px-4">Use a real JSON document to validate your rules, use <strong className="text-green-500">$</strong> to reference to this document</p>
                                                            <Button disabled={state.exampleLoading} variant="contained" color="primary" className="h-14" onClick={fetchRandomDocumentExample} > {state.exampleLoading ? <CircularProgress size={16} className="!text-white" /> : "Get Random Example"} </Button>
                                                        </div>
                                                    )}
                                                    {state.tab === 1 && (
                                                        <div className="grid gap-2 p-4">
                                                            <Box className="h-full !w-full overflow-hidden">
                                                                <Paper className="h-full !w-full overflow-hidden">
                                                                    <Editor
                                                                        options={{
                                                                            selectOnLineNumbers: true,
                                                                            automaticLayout: true,
                                                                            minimap: {
                                                                                enabled: false
                                                                            },
                                                                            lineNumbers: 'on'
                                                                        }}
                                                                        height="300px"
                                                                        language="json"
                                                                        theme="git"
                                                                        className="h-full !w-full !overflow-hidden"
                                                                        value={state.previousExample}
                                                                        onChange={(newValue, e) => onChange({ target: { name: "previousExample", type: "text", value: newValue } })}
                                                                    />
                                                                </Paper>
                                                            </Box>
                                                            <p className="text-gray-700 text-xs px-4">Use a real JSON document to validate your rules, use <strong className="text-green-500">$prev</strong> to reference to this document</p>
                                                            <Button loading disabled={state.exampleLoading} variant="contained" color="primary" className="h-14" onClick={fetchRandomDocumentPreviousExample} > {state.exampleLoading ? <CircularProgress size={16} className="!text-white" /> : "Get Random Example"} </Button>
                                                        </div>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Box>

                                <Box className="grid ">
                                    <div className="flex flex-col gap-4">
                                        <FormControl required>
                                            <TextField
                                                required
                                                style={{ fontSize: "12px" }}
                                                size="medium"
                                                name="name"
                                                label="Rule Name"
                                                multiline
                                                value={state.name}
                                                onChange={onChangeRule}
                                                helperText={!state.name ? "This field is required" : ""}
                                            />
                                        </FormControl>

                                        <FormControl required variant="outlined" size="large" fullWidth={true}>
                                            <TextField
                                                required
                                                style={{ fontSize: "12px" }}
                                                size="medium"
                                                name="description"
                                                label="Rule Description"
                                                multiline
                                                rows={2}
                                                value={state.description}
                                                helperText={"This description is helpful for to user to identify the importance of the rule"}
                                                onChange={onChangeRule}
                                            />
                                        </FormControl>

                                        <div className="flex-1 pb-4">
                                            <Box className="h-full !w-full ">
                                                <Paper className="h-full !w-full ">
                                                    <Editor
                                                        ref={completionProviderRef}
                                                        options={{
                                                            selectOnLineNumbers: true,
                                                            automaticLayout: true,
                                                            minimap: {
                                                                enabled: false
                                                            },
                                                            lineNumbers: 'on',
                                                            scrollbar: {
                                                                vertical: 'auto',
                                                                horizontal: "auto",
                                                            },
                                                            ariaLabel: 'required',
                                                            placeholder: '$.field',
                                                        }}
                                                        height="200px"
                                                        width="100%"
                                                        language="javascript"
                                                        defaultLanguage="javascript"
                                                        theme="light"
                                                        className="h-full !w-full"
                                                        value={state.expression}
                                                        onMount={handleEditorDidMount}
                                                        onChange={(newValue, e) => onChange({ target: { name: "expression", type: "text", value: newValue } })}
                                                    />
                                                </Paper>
                                            </Box>
                                        </div>
                                    </div>
                                    {testResult ? <span className={testResult.valid ? "bg-green-500 text-white rounded-sm py-1 px-2" : "bg-red-500 text-white rounded-sm py-1 px-2"}>{testResult.message}<br />Result: {`${testResult.result}`}</span> : <div />}
                                </Box>
                            </div>

                            <div className="border-b border-gray-300 my-4" />

                            <div className="grid gap-4">

                                <FormControl component="fieldset">
                                    <FormGroup aria-label="position" row>
                                        <FormControlLabel
                                            value={state.useCustomPayload}
                                            control={<Checkbox checked={state.useCustomPayload} name="useCustomPayload" />}
                                            label="Use custom payload"
                                            onChange={() => setState({ ...state, useCustomPayload: !state.useCustomPayload })}
                                        />
                                    </FormGroup>
                                    <FormHelperText>By default, notifications carry the payload of the document that changed, however, it is possible to customize the payload to add or remove fields, or even perform unions (joins) with other documents.</FormHelperText>
                                </FormControl>

                                {state.useCustomPayload && (
                                    <div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex-1 pb-4">
                                                <Box className="h-full !w-full ">
                                                    <Paper className="h-full !w-full ">
                                                        <Editor
                                                            options={{
                                                                selectOnLineNumbers: true,
                                                                automaticLayout: true,
                                                                minimap: {
                                                                    enabled: false
                                                                },
                                                                lineNumbers: 'on',
                                                                scrollbar: {
                                                                    vertical: 'auto',
                                                                    horizontal: "auto",
                                                                },
                                                                ariaLabel: 'required',
                                                                placeholder: '$.field',
                                                            }}
                                                            height="300px"
                                                            width="100%"
                                                            language="javascript"
                                                            defaultLanguage="javascript"
                                                            theme="light"
                                                            className="h-full !w-full"
                                                            value={state.customPayloadExpression}
                                                            onChange={(newValue, e) => onChange({ target: { name: "customPayloadExpression", type: "text", value: newValue } })}
                                                        />
                                                    </Paper>
                                                </Box>
                                                <p className="text-gray-700 text-xs px-4 pt-2">Use the simbol <strong className="text-green-500">$</strong> to reference to the changed document</p>
                                            </div>

                                            <div className="flex-1 pb-4">
                                                <Box className="h-full !w-full ">
                                                    <Paper className="h-full !w-full ">
                                                        <Editor
                                                            options={{
                                                                readOnly: true,
                                                                selectOnLineNumbers: true,
                                                                automaticLayout: true,
                                                                minimap: {
                                                                    enabled: false
                                                                },
                                                                lineNumbers: 'on',
                                                                scrollbar: {
                                                                    vertical: 'auto',
                                                                    horizontal: "auto",
                                                                },
                                                                ariaLabel: 'required',
                                                                placeholder: '$.field',
                                                            }}
                                                            height="300px"
                                                            width="100%"
                                                            language="json"
                                                            defaultLanguage="json"
                                                            theme="light"
                                                            className="h-full !w-full"
                                                            value={testPayloadResult?.result ? JSON.stringify(testPayloadResult.result, null, 2) : ""}
                                                        />
                                                    </Paper>
                                                </Box>
                                            </div>
                                            {testPayloadResult ? <span className={testPayloadResult.valid ? "bg-green-500 text-white rounded-sm py-1 px-2" : "bg-red-500 text-white rounded-sm py-1 px-2"}>{testPayloadResult.message}<br />Result: {`${testPayloadResult.result}`}</span> : <div />}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </AccordionDetails>
                    <AccordionActions className="bg-gray-100 border-t w-full">
                        <div className="flex flex-row items-center justify-between w-full">
                            <div className="flex flex-row gap-4 justify-end w-full">
                                <Tooltip title="Test rule">
                                    <Button variant="contained" color="primary" className="" disabled={state.autoEvaluate} onClick={() => validateExpression()} >
                                        <PlaylistAddCheckCircleIcon />
                                        <span className="ml-1 text-white">EVALUATE</span>
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Save rule">
                                    <Button variant="contained" color="primary" className="" onClick={() => onSave()} ><span className="ml-1 text-white">SAVE</span></Button>
                                </Tooltip>
                            </div>
                        </div>
                    </AccordionActions>
                </Accordion>
            </div>
        </div>
    )
}

export default EditCollectionRule